import { classes } from "./classes.js";

/**
 * `P0-UIK-001`: initials from a name, because this product has no photographs
 * of its users and inventing an avatar service for a demo would be waste.
 *
 * The initials are computed from the FIRST and LAST word, so "Awa Traoré"
 * gives AT and a single name gives one letter rather than throwing. Accented
 * initials are kept as they are , `É` is a letter, not a decoration.
 */
export type AvatarSize = "sm" | "md";

export interface AvatarProps {
  readonly name: string;
  readonly size?: AvatarSize;
  readonly className?: string;
}

const SIZES: Readonly<Record<AvatarSize, string>> = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
};

export function initials(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0]?.at(0) ?? "";
  const last = words.length > 1 ? (words.at(-1)?.at(0) ?? "") : "";
  return (first + last).toLocaleUpperCase("fr-FR");
}

export function Avatar({ name, size = "md", className }: AvatarProps): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={classes(
        "inline-flex items-center justify-center rounded-full bg-indigo-50 font-medium text-indigo-600",
        SIZES[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
